import { useAnalytics } from '@sixfold/app-analytics-framework';
import {
  LanguagePicker as CommonUiLanguagePicker,
  LanguagePickerItem,
  LanguagePickerItemData,
} from '@sixfold/common-ui';
import { Localized, i18n, useLanguage, useLocalizations } from '@sixfold/localization-component';
import { notNil } from '@sixfold/typed-primitives';
import * as React from 'react';

import { EmbedConfig } from '../../../shared/types';
import { CustomerSupportAvailableLanguage } from '../../customer_support_ticket/components/PlatformSupportLanguageSelect';
import { EmbedDataContext } from '../embed_data_context';

const DEFAULT_NOTIFICATIONS_SUPPORTED_LANGUAGE: AvailableLanguage = 'en';

export const supportedLanguages = {
  bg: 'Български',
  cs: 'Čeština',
  de: 'Deutsch',
  en: 'English',
  'en-US': 'English (US)',
  es: 'Español',
  fr: 'Français',
  hr: 'Hrvatski',
  it: 'Italiano',
  hu: 'Magyar',
  nl: 'Nederlands',
  pl: 'Polski',
  pt: 'Português',
  ro: 'Română',
  ru: 'Pусский',
  sk: 'Slovenčina',
  fi: 'Suomi',
  sv: 'Svenska',
  tr: 'Türkçe',
  uk: 'Українська',
};

export type AvailableLanguage = keyof typeof supportedLanguages;
export const availableLanguages = Object.keys(supportedLanguages);

export function useNotificationApiLanguageOptions(): { value: string; label: string }[] {
  const { embedConfig } = React.useContext(EmbedDataContext);
  const translatedLanguages = useLanguagePickerLocalizations();

  return React.useMemo(
    () =>
      getNotificationApiSupportedLanguages(embedConfig?.notification_api_supported_languages)
        .map((languageKey) => {
          const label = translatedLanguages[`languagePicker.language.${languageKey}`];
          return label ? { value: languageKey, label } : undefined;
        })
        .filter(notNil),
    [embedConfig?.notification_api_supported_languages, translatedLanguages],
  );
}

export const getNotificationApiSupportedLanguages = (
  notificationApiSupportedLanguages?: EmbedConfig['notification_api_supported_languages'],
) => {
  return Object.keys(supportedLanguages).filter(
    (languageKey) =>
      notificationApiSupportedLanguages === undefined || notificationApiSupportedLanguages.includes(languageKey),
  );
};

export function useNotificationApiDefaultLanguage(
  fallbackLanguage: AvailableLanguage = DEFAULT_NOTIFICATIONS_SUPPORTED_LANGUAGE,
): string {
  const currentLanguage = useLanguage();
  const { embedConfig } = React.useContext(EmbedDataContext);
  const notificationApiSupportedLanguages = getNotificationApiSupportedLanguages(
    embedConfig?.notification_api_supported_languages,
  );

  return (notificationApiSupportedLanguages as string[]).includes(currentLanguage) ? currentLanguage : fallbackLanguage;
}

export const useLanguagePickerLocalizations = (): {
  [key in AvailableLanguage | CustomerSupportAvailableLanguage as `languagePicker.language.${key}`]: string;
} => {
  return useLocalizations({
    'languagePicker.language.bg': 'Bulgarian',
    'languagePicker.language.cs': 'Czech',
    'languagePicker.language.de': 'German',
    'languagePicker.language.en': 'English',
    'languagePicker.language.en-US': 'English (US)',
    'languagePicker.language.es': 'Spanish',
    'languagePicker.language.fr': 'French',
    'languagePicker.language.hr': 'Croatian',
    'languagePicker.language.it': 'Italian',
    'languagePicker.language.hu': 'Hungarian',
    'languagePicker.language.nl': 'Dutch',
    'languagePicker.language.pl': 'Polish',
    'languagePicker.language.pt': 'Portuguese',
    'languagePicker.language.ro': 'Romanian',
    'languagePicker.language.ru': 'Russian',
    'languagePicker.language.sk': 'Slovak',
    'languagePicker.language.fi': 'Finnish',
    'languagePicker.language.sv': 'Swedish',
    'languagePicker.language.tr': 'Turkish',
    'languagePicker.language.uk': 'Ukrainian',
    'languagePicker.language.sr': 'Serbian',
    'languagePicker.language.sl': 'Slovenian',
  });
};

interface Props {
  changeLocaleMutation?: (locale: string) => Promise<void>;
  trigger?: ({
    formattedLabel,
    selectedItem,
  }: {
    label: React.ReactNode;
    formattedLabel: React.ReactNode;
    selectedItem: LanguagePickerItemData | null;
  }) => React.ReactElement;
}

export const LanguagePicker: React.FunctionComponent<Props> = ({ changeLocaleMutation, trigger }) => {
  const { embedConfig } = React.useContext(EmbedDataContext);
  const analytics = useAnalytics();
  const currentLanguage = useLanguage();

  const enableDevLanguage = embedConfig !== null && embedConfig.enable_dev_language;

  return (
    <CommonUiLanguagePicker
      label={<Localized id="component.languagepicker.language.title">Language</Localized>}
      value={currentLanguage}
      trigger={trigger}
      onChange={async (lang) => {
        if (changeLocaleMutation !== undefined) {
          await changeLocaleMutation(lang);
        }

        if (i18n.isInitialized) {
          await i18n.changeLanguage(lang);
        }

        if (analytics) {
          analytics.track('Language changed', { previousLanguage: currentLanguage, currentLanguage: lang });
        }
      }}>
      {Object.keys(supportedLanguages).map((lang) => {
        return <LanguagePickerItem key={lang} value={lang} label={supportedLanguages[lang]} />;
      })}
      {enableDevLanguage && <LanguagePickerItem value="cimode" label="Dev language" />}
    </CommonUiLanguagePicker>
  );
};
